import { useUserStore } from '@store/user';

/**
 * The CRM half of the Company Diary gates (design D13).
 *
 * These mirror `company-diary-view` / `-modify` / `-manage-types` in
 * `AuthServiceProvider`. There is no synced permission list on this platform, so
 * the role sets are written out by hand on both sides - which means **these are a
 * UI convenience, never the enforcement**. Every endpoint checks the real gate; the
 * job here is to stop offering buttons that would be refused.
 *
 * Keep the three lists identical to the gates. A CRM list that drifts wider shows a
 * user an action that 403s; one that drifts narrower hides work they are allowed to
 * do, which is the harder bug to notice.
 */

/** Reads the whole tenant's diary. Sales are here: a truck being off the road matters when quoting. */
const VIEW_ROLES = ['SUPER', 'MOVEPRO_ADMIN', 'DEVELOPER', 'OWNER', 'ADMIN', 'MANAGER', 'OPS', 'SALES'] as const;

/**
 * Writes entries. Sales read but do not write; field staff get neither - booking
 * your own leave unsupervised is a leave-approval workflow, which v1 does not have.
 */
const MODIFY_ROLES = ['SUPER', 'MOVEPRO_ADMIN', 'DEVELOPER', 'OWNER', 'ADMIN', 'MANAGER', 'OPS'] as const;

/**
 * Edits the type catalogue. Narrower than modify because a type carries
 * `blocks_availability`: renaming one is cosmetic, flipping that flag silently
 * imposes or releases availability blocks across every entry using it.
 */
const MANAGE_TYPE_ROLES = ['SUPER', 'MOVEPRO_ADMIN', 'DEVELOPER', 'OWNER', 'ADMIN', 'MANAGER'] as const;

/**
 * Roles admitted to the diary only for their *own* entries - the ones they created
 * or are assigned to. The server narrows the listing for them; the CRM uses this to
 * label the page rather than to filter, since filtering client-side would be a
 * second, drifting implementation of the same rule.
 */
const OWN_ENTRIES_ONLY_ROLES = ['FIELD_STAFF', 'CONTRACTOR'] as const;

export function useCompanyDiaryPermissions() {
    const userStore = useUserStore();

    const hasAny = (roles: readonly string[]): boolean => roles.some((role) => userStore.hasRole(role));

    const canViewOwnEntriesOnly = computed(() => !hasAny(VIEW_ROLES) && hasAny(OWN_ENTRIES_ONLY_ROLES));

    return {
        canView: computed(() => hasAny(VIEW_ROLES) || canViewOwnEntriesOnly.value),
        canModify: computed(() => hasAny(MODIFY_ROLES)),
        canManageTypes: computed(() => hasAny(MANAGE_TYPE_ROLES)),
        canViewOwnEntriesOnly,
    };
}
